'use strict';

/**
 * Model for a catalogs
 *
 * @category   Model
 * @module     collection
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Model = require('base/Colido.Model');
var ObjectsCollection = require('../collections/objects');
var SetsCollection = require('../modules/set/collections/sets');
var StoragesCollection = require('../modules/location/collections/storages');
var ImportsCollection = require('../modules/import/collections/imports');
var TrashCollection = require('../modules/trash/collections/trash');

var CatalogModel = Model.extend({
    api: '/catalogs',

    initialize: function() {
        this.objects = new ObjectsCollection(null, {
            catalog: this,
        });

        this.sets = new SetsCollection(null, {
            catalog: this,
        });

        this.storages = new StoragesCollection(null, {
            catalog: this,
        });

        this.imports = new ImportsCollection(null, {
            catalog: this,
        });

        this.trash = new TrashCollection(null, {
            catalog: this,
        });

        this.setSearchFields();
        this.setFilterComponents();
    },

    setSearchFields: function() {
        var searchFields = [];
        _.each(this.get('fields'), function(field) {
            if (field.searchable) {
                searchFields.push(field.name);
            }
        })

        this.objects.searchFields = searchFields;
    },

    setFilterComponents: function() {
        var filterFields = [];

        _.each(this.get('fieldsFilterOrder'), function(fielNamedOrSection) {
            if (_.isArray(fielNamedOrSection)) {
                var section = [];
                _.each(fielNamedOrSection, function(fieldName) {
                    var field = this.getFieldDefinition(fieldName);
                    if (field && field.filter) {
                        var filterComponent = field.filter;
                        filterComponent.options = _.extend({}, field.options, filterComponent.options);

                        section.push(filterComponent)
                    }
                }.bind(this));
                filterFields.push(section)
            } else {
                var fieldName = fielNamedOrSection;
                var field = this.getFieldDefinition(fieldName);
                if (field && field.filter) {
                    var filterComponent = field.filter;

                    // filterComponent.options = field.options || {};

                    section.push(filterComponent)
                }
            }
        }.bind(this));

        this.objects.filterComponents = filterFields;
    },

    getActiveTableFields: function() {
        return _.where(this.getTableFields(), {active: true});
    },

    getTableFields: function() {
        var tableFields = [];

        _.each(this.get('fieldsTableOrder'), function(field){
            var fieldName = field.fieldName;
            var isFieldActive = field.active;

            field = this.getFieldDefinition(fieldName);

            if (!field) {
                console.warn('Missing field definition for', fieldName);

                return;
            }

            if (!field.tableview) {
                console.warn('Missing tableview definition for field', fieldName);

                return;
            }

            tableFields.push({
                name: fieldName,
                type: field.displayType,
                label: field.tableview.label,
                tableview: field.tableview,
                unitField: field.unitField || null,
                sourceFields: field.sourceFields || null,
                options: field.options || {},
                active: isFieldActive,
            })
        }.bind(this));

        return tableFields;
    },

    getActiveEditFields: function() {
        return _.where(this.getEditFields(), {active: true});
    },

    getEditFields: function() {
        var editFields = [];

        _.each(this.get('fieldsEditOrder'), function(field){
            var fieldName = field.fieldName;
            var editField = this.getEditField(fieldName);
            editField.active = field.active;

            if (_.isString(field.disabledSourceFields)) {
                editField.disabledSourceFields = field.disabledSourceFields.split(',');
            }

            //Only show scanner fields to users with proper license
            if (editField.type === 'scanners' && App.user.get('licenseScanners') !== true) {
                return;
            }

            editFields.push(editField)
        }.bind(this));

        return editFields;
    },

    getEditField: function(fieldName) {
        var field = this.getFieldDefinition(fieldName);

        if (!field) {
            console.warn('Missing field definition for', fieldName);

            return;
        }

        if (!field.editview) {
            console.warn('Missing editview definition for field', fieldName);

            return;
        }

        return {
            name: fieldName,
            type: field.displayType,
            label: field.editview.label,
            placeholder: field.editview.placeholder,
            unitField: field.unitField || null,
            sourceFields: field.sourceFields || null,
            options: field.options || {},
            editOptions: field.editview.options || {},
        };
    },

    getDetailFields: function(options) {
        options = options || {};

        var orderArray = options.isShowroom ? 'fieldsShowroomOrder' : 'fieldsDetailOrder';
        var shareDetailFields = null;

        if (options.shareModel) {
            shareDetailFields = options.shareModel.get('activeDetailFields');
        }

        var detailFields = [];
        _.each(this.get(orderArray), function(sectionFields) {
            var section = [];
            _.each(sectionFields, function(fieldName) {
                if (shareDetailFields) { //if we have a shareModel
                    if (_.contains(shareDetailFields, fieldName)) { //only return active fields
                        section.push(this.getDetailField(fieldName));
                    }
                } else {
                    section.push(this.getDetailField(fieldName));
                }
            }.bind(this));

            if (section.length) {
                detailFields.push(section);
            }
        }.bind(this));

        return detailFields;
    },

    getDetailField: function(fieldName) {
        var field = this.getFieldDefinition(fieldName);

        if (!field) {
            console.warn('Missing field definition for', fieldName);

            return;
        }

        if (!field.detailview) {
            console.warn('Missing detailview definition for field', fieldName);

            return;
        }

        return {
            name: fieldName,
            type: field.displayType,
            label: field.detailview.label,
            unitField: field.unitField || null,
            sourceFields: field.sourceFields || null,
            options: field.options || {},
        };
    },

    getFilterField: function(filterName) {
        var fields = this.get('fields');
        var field = _.find(fields, function(field){
            return field.filter && field.filter.name === _.camelToHyphen(filterName);
        });

        if (!field) {
            console.warn('Missing filter definition for', filterName);
        }

        return field;
    },

    getFieldDefinition: function(fieldName) {
        var fields = this.get('fields');

        if (_.isString(fieldName)) {
            fieldName = fieldName.split('.').shift(); //only take main part of fieldName, deep parts indicated by . are cut off
        }

        return _.findWhere(fields, {name: fieldName});
    },

    getGridViewSortableFields: function() {
        var fields = this.get('fields');
        var sortableFields = _.filter(fields, function(field){
            return field.gridview && field.gridview.sortable === true;
        })

        return sortableFields;
    },

    getImportFields: function() {
        var fields = [];

        _.each(this.get('fieldsImportOrder'), function(importField) {
            var field = this.getFieldDefinition(importField);

            if (!field) {
                console.warn('Nonexisting field specified for import:', importField);

                return;
            }

            if (!field.import) {
                console.warn('Missing import definition for field:', importField);

                return;
            }

            if (field.import.displayName) {
                fields.push({
                    fieldName: field.name,
                    name: field.import.displayName,
                    isMultipleImport: !!field.import.isMultipleImport,
                });
            }

            if (field.import.subfields) {
                _.each(field.import.subfields, function(subfield) {
                    fields.push({
                        fieldName: field.name + '.' + subfield.name,
                        name: subfield.displayName,
                        isMultipleImport: !!subfield.isMultipleImport,
                    });
                });
            }
        }.bind(this));

        return fields;
    },

    getExportFields: function(type) {
        var fields = [];

        var orderArray = 'fieldsExportOrder';

        if (type === 'print') {
            orderArray = 'fieldsPrintOrder';
        }

        _.each(this.get(orderArray), function(exportField) {
            var fieldName = exportField.fieldName;
            var isFieldActive = exportField.active;

            var field = this.getFieldDefinition(fieldName);

            if (!field) {
                console.warn('Nonexisting field specified for export:', fieldName);

                return;
            }

            if (!field.export) {
                console.warn('Missing export definition for field:', fieldName);

                return;
            }

            fields.push({
                fieldName: field.name,
                label: field.export.label,
                deactivatable: field.export.deactivatable,
                active: isFieldActive,
            });
        }.bind(this));

        return fields;
    },

    getPrintFields: function() {
        return this.getExportFields('print');
    },

    getObjectDefaults: function() {
        var fields = this.get('fields');

        var defaultValues = {};
        _.each(fields, function(field){
            defaultValues[field.name] = field.defaultValue;
        })

        return defaultValues;
    },
});

module.exports = CatalogModel;